import { useEffect, useState } from "react";
import { FamilyStatus } from "protogen/advisors_service_pb";
import { useNavigate, useParams } from "react-router-dom";
import { LEARNING_STEPS } from "components/member/learningSteps";
import LearningStepBanner from "components/member/LearningStepBanner";

const STEP = LEARNING_STEPS.find((s) => s.key === "core-memories")!;
import { Box, Button, MenuItem, Typography } from "@mui/material";
import {
  ListDocumentsRequest,
  UpdateDocumentRequest,
  CreateMemberDocumentRequest,
} from "protogen/documents_service_pb";
import {
  Document,
  DocumentScope,
  DocumentType,
  DocumentStatus,
} from "protogen/documents_pb";
import FlexPage from "components/layout/FlexPage";
import useIsMobile from "components/hooks/useIsMobile";
import {
  useCreateMemberDocument,
  useListMemberDocuments,
  useUpdateDocument,
} from "services/documents";
import SearchBar from "components/common/SearchBar";
import DocumentsTable from "components/documents/DocumentsTable";
import ConfirmationDialog, {
  useConfirmationDialog,
} from "components/common/ConfirmationDialog";
import Loading from "components/common/Loading";
import Breadcrumbs from "components/common/Breadcrumbs";
import useTitle from "components/hooks/useTitle";
import { StyledMenu } from "components/common/DropdownMenu";
import { useMemberDetails } from "components/context/MemberDetailsContext";
import { useUpdateProductPrompts } from "services/nux";
import { ChevronDown, Cpu } from "lucide-react";
import LinkRouter from "components/navigation/LinkRouter";
import DateDisplay from "components/common/DateDisplay";

const BANNER_DISMISS_KEY = "learningStep_core-memories_dismissed";

const CreateButton = ({
  onCreateDocument,
  onCreateList,
  isMobile,
}: {
  onCreateDocument: () => void;
  onCreateList: () => void;
  isMobile: boolean;
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [open, setOpen] = useState(false);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    setOpen(true);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setOpen(false);
  };

  return (
    <Box>
      <Button
        variant="contained"
        color="primary"
        onClick={handleClick}
        endIcon={<ChevronDown size={16} />}
      >
        {isMobile ? "Create" : "Create document"}
      </Button>
      <StyledMenu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        <MenuItem
          onClick={() => {
            handleClose();
            onCreateDocument();
          }}
        >
          Document
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleClose();
            onCreateList();
          }}
        >
          List
        </MenuItem>
      </StyledMenu>
    </Box>
  );
};

export default () => {
  useTitle("Documents");
  const params = useParams();
  const isMobile = useIsMobile();
  const {
    data: memberData,
    isDigitalSubscription,
    familyStatus,
  } = useMemberDetails();
  const isDeactivated =
    familyStatus === FamilyStatus.DEACTIVATED ||
    familyStatus === FamilyStatus.DIGITAL_DEACTIVATED ||
    familyStatus === FamilyStatus.DIGITAL_DEACTIVATED_TRIAL ||
    familyStatus === FamilyStatus.DEACTIVATED_PROSPECT;
  const { request: updateProductPrompts } = useUpdateProductPrompts();
  const coreDocuments = isDigitalSubscription
    ? memberData?.coreDocuments ?? []
    : [];
  const productPrompts = memberData?.member?.productPrompts
    ? JSON.parse(memberData.member.productPrompts)
    : {};
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const showBanner =
    isDigitalSubscription &&
    !bannerDismissed &&
    productPrompts[BANNER_DISMISS_KEY] !== "dismissed";

  const handleDismissBanner = async () => {
    setBannerDismissed(true);
    await updateProductPrompts({
      key: BANNER_DISMISS_KEY,
      value: "dismissed",
    });
  };
  const [allDocuments, setAllDocuments] = useState<Document[]>([]);
  const [filteredAndSortedDocuments, setFilteredAndSortedDocuments] = useState<
    Document[] | null
  >(null);
  const [query, setQuery] = useState<string>("");
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const { request: listDocumentsRequest } = useListMemberDocuments();
  const confirmState = useConfirmationDialog();
  const { request: createDocumentRequest } = useCreateMemberDocument();
  const { request: updateDocumentRequest } = useUpdateDocument();
  const navigate = useNavigate();

  const createDocument = async () => {
    const result = await createDocumentRequest();
    navigate(`/documents/${encodeURIComponent(result?.document?.uuid || "")}`);
  };

  const createList = async () => {
    const result = await createDocumentRequest(
      new CreateMemberDocumentRequest({
        documentType: DocumentType.LIST,
      }),
    );
    navigate(`/documents/${encodeURIComponent(result?.document?.uuid || "")}`);
  };

  const onDeleteDocument = (document: Document) => {
    confirmState.openDialog(async () => {
      await updateDocumentRequest(
        new UpdateDocumentRequest({
          documentRef: document.ref,
          status: DocumentStatus.DELETED,
          prevHash: "",
        }),
      );
      listDocuments();
    });
  };

  const onLoad = async () => {
    listDocuments();
  };

  const listDocuments = async () => {
    const topics = params.topics
      ? decodeURIComponent(params.topics).split(",")
      : [];

    const r = await listDocumentsRequest(
      new ListDocumentsRequest({
        documentTypes: [
          DocumentType.NOTE,
          DocumentType.TASK_BLOCK,
          DocumentType.LIST,
        ],
        favoritesOnly: false,
        includeCommunitySharedDocuments: false,
        anyTopics: topics,
      }),
    );
    let documents = r?.documents || [];
    setAllDocuments(documents);
    setHasLoadedOnce(true);
  };

  const filterDocuments = (documents: Document[], searchQuery: string) => {
    let filtered = isDigitalSubscription
      ? documents.filter((doc) => doc.scope !== DocumentScope.FAMILY_BOT)
      : documents;
    if (!searchQuery.trim()) return filtered;
    const lowercaseQuery = searchQuery.toLowerCase();
    return filtered.filter(
      (doc) =>
        doc.title?.toLowerCase().includes(lowercaseQuery) ||
        doc.topics?.some((topic) =>
          topic.toLowerCase().includes(lowercaseQuery),
        ),
    );
  };

  useEffect(() => {
    if (!hasLoadedOnce) return;
    const filtered = filterDocuments(allDocuments, query);
    setFilteredAndSortedDocuments(filtered);
  }, [allDocuments, query, hasLoadedOnce]);

  useEffect(() => {
    setFilteredAndSortedDocuments(null);
    onLoad();
  }, []);

  return (
    <FlexPage leftAligned sx={{ width: "100%" }}>
      <Breadcrumbs
        breadcrumbs={[{ name: "Home", link: "/" }, { name: "Documents" }]}
      />
      <Typography variant="display" sx={{ marginBottom: "8px" }}>
        Documents
      </Typography>
      {isDigitalSubscription && (
        <Box
          sx={{
            marginBottom: "32px",
            padding: "20px",
            borderRadius: "16px",
            backgroundColor: "#F9FAFB",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              marginBottom: "12px",
            }}
          >
            <Cpu size={16} color="#198282" />
            <Typography
              sx={{
                fontSize: "12px",
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.6px",
                color: "rgba(0,0,0,0.5)",
              }}
            >
              Core Memories
            </Typography>
          </Box>
          {showBanner && (
            <LearningStepBanner step={STEP} onDismiss={handleDismissBanner} />
          )}
          <Box sx={{ display: "flex", flexDirection: "column", gap: "6px" }}>
            {coreDocuments.length === 0 ? (
              <Typography variant="body" color="text.secondary">
                These files shape how your Faye agent understands your
                household. They'll appear here once your agent starts learning
                about you.
              </Typography>
            ) : (
              coreDocuments.map((doc, index) => (
                <Box
                  key={`${doc.documentRef}-${index}`}
                  component={LinkRouter}
                  to={`/documents/${doc.documentRef}`}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    padding: "10px 14px",
                    borderRadius: "10px",
                    backgroundColor: "#FFF",
                    border: "1px solid #EAECF0",
                    cursor: "pointer",
                    textDecoration: "none",
                    color: "inherit",
                  }}
                >
                  <Cpu
                    size={15}
                    strokeWidth={2}
                    color="#198282"
                    style={{ flexShrink: 0 }}
                  />
                  <Typography
                    variant="body"
                    color="text.primary"
                    sx={{ flex: 1, minWidth: 0 }}
                  >
                    {doc.title}
                  </Typography>
                  {Number(doc.lastUpdatedSec) > 0 && (
                    <DateDisplay
                      date={new Date(Number(doc.lastUpdatedSec) * 1000)}
                      useRelativeAsPrimary={false}
                      sx={{ flexShrink: 0 }}
                    />
                  )}
                </Box>
              ))
            )}
          </Box>
        </Box>
      )}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "32px",
          width: "100%",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            gap: "16px",
            justifyContent: "space-between",
          }}
        >
          <SearchBar
            fullWidth={isMobile}
            onQuery={(query) => {
              setQuery(query);
            }}
            onKeyPress={true}
            sx={{ marginLeft: "0px" }}
          />
          {!isDeactivated && (
            <CreateButton
              onCreateDocument={createDocument}
              onCreateList={createList}
              isMobile={isMobile}
            />
          )}
        </Box>

        {!filteredAndSortedDocuments ? (
          <Loading />
        ) : (
          <DocumentsTable
            documents={filteredAndSortedDocuments}
            layout="simple"
            showFavoriteCount={false}
            clientView={true}
            onDelete={onDeleteDocument}
          />
        )}
      </Box>

      <ConfirmationDialog
        content={"Are you sure you want to remove this document?"}
        {...confirmState.dialogProps}
      />
    </FlexPage>
  );
};
